import { Dispatch, useEffect, useState, useRef, SetStateAction } from "react";
import { useSelector } from "react-redux";
import { ErrorMessage } from "@tvg/wallet/src/types";
import { getAccountNumber } from "@urp/store-selectors";
import { createBraintreeClient } from "../utils/braintreeClient";
import { getBraintreeCCToggle } from "../redux/toggles";
import {
  BRAINTREE_ERROR_CODES,
  BRAINTREE_SDK_SCRIPT_LOAD_TIMEOUT_MS,
  BRAINTREE_SDK_SCRIPT_POLL_INTERVAL_MS
} from "../constants";
import { fetchAndValidateClientToken } from "./useBraintreeHostedFields";
import type {
  BraintreeClientInstance,
  BraintreeDataCollectorInstance
} from "../types/braintree";
import "../types/braintree";

interface UseBraintreeDataCollectorProps {
  isActive: boolean;
  /** When set, SDK load timeout / init failures surface `BRAINTREE_INIT_ERROR` (same as hosted fields); user retries manually (e.g. close/reopen modal). */
  setErrorMessage?: Dispatch<SetStateAction<ErrorMessage>>;
}

const showBraintreeInitError = (
  setErrorMessage?: Dispatch<SetStateAction<ErrorMessage>>
) => {
  setErrorMessage?.((prevState) => ({
    ...prevState,
    isOpen: true,
    errorCode: BRAINTREE_ERROR_CODES.INIT_ERROR,
    isRetry: false
  }));
};

/**
 * Lightweight hook that initialises only the Braintree data-collector
 * (no hosted fields). Use for vault deposits where we still need
 * deviceData for fraud screening but aren't collecting card details.
 */
export const useBraintreeDataCollector = ({
  isActive,
  setErrorMessage
}: UseBraintreeDataCollectorProps) => {
  const accountId = useSelector(getAccountNumber) as string;
  const isBraintreeCCEnabled = useSelector(getBraintreeCCToggle) as boolean;
  const [deviceData, setDeviceData] = useState("");
  const [isSdkAvailable, setIsSdkAvailable] = useState(
    typeof window !== "undefined" && !!window.braintree?.dataCollector
  );
  const clientRef = useRef<BraintreeClientInstance | null>(null);
  const collectorRef = useRef<BraintreeDataCollectorInstance | null>(null);

  // Scripts may load after first paint; poll + timeout match useBraintreeHostedFields.
  useEffect(() => {
    if (
      isSdkAvailable ||
      !isActive ||
      !isBraintreeCCEnabled ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (window.braintree?.dataCollector) {
        clearTimeout(timeoutId);
        setIsSdkAvailable(true);
        clearInterval(intervalId);
      }
    }, BRAINTREE_SDK_SCRIPT_POLL_INTERVAL_MS);

    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      showBraintreeInitError(setErrorMessage);
    }, BRAINTREE_SDK_SCRIPT_LOAD_TIMEOUT_MS);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [isSdkAvailable, isActive, isBraintreeCCEnabled, setErrorMessage]);

  useEffect(() => {
    if (
      !isActive ||
      !isBraintreeCCEnabled ||
      !accountId ||
      typeof window === "undefined" ||
      !isSdkAvailable ||
      !window.braintree?.dataCollector
    ) {
      return undefined;
    }

    const abortController = new AbortController();
    const noopSetError = (() => {}) as Dispatch<SetStateAction<ErrorMessage>>;

    const init = async () => {
      try {
        const result = await fetchAndValidateClientToken(
          accountId,
          setErrorMessage ?? noopSetError,
          abortController.signal
        );
        if (!result || abortController.signal.aborted) return;

        const clientInstance = await createBraintreeClient(result.token);
        if (abortController.signal.aborted) {
          clientInstance.teardown().catch(() => {});
          return;
        }
        clientRef.current = clientInstance;

        if (!window.braintree?.dataCollector) return;

        const collector = await window.braintree.dataCollector.create({
          client: clientInstance
        });
        if (abortController.signal.aborted) {
          collector.teardown().catch(() => {});
          return;
        }
        collectorRef.current = collector;
        setDeviceData(collector.deviceData);
      } catch {
        if (abortController.signal.aborted) {
          return;
        }
        if (collectorRef.current) {
          collectorRef.current.teardown().catch(() => {});
          collectorRef.current = null;
        }
        if (clientRef.current) {
          clientRef.current.teardown().catch(() => {});
          clientRef.current = null;
        }
        showBraintreeInitError(setErrorMessage);
      }
    };

    init();

    return () => {
      abortController.abort();
      if (collectorRef.current) {
        collectorRef.current.teardown().catch(() => {});
        collectorRef.current = null;
      }
      if (clientRef.current) {
        clientRef.current.teardown().catch(() => {});
        clientRef.current = null;
      }
      setDeviceData("");
    };
  }, [
    isActive,
    isBraintreeCCEnabled,
    accountId,
    isSdkAvailable,
    setErrorMessage
  ]);

  return deviceData;
};
